import { AlertModal, DataTable, IconComponent } from '@/components/Common';
import { Loader } from '@/components/Common/Loader';
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { getUnitWorkloadAcademicTeachings, getUnitWorkloadTeachingHours, useHandleError } from '@/hooks';
import { CustomTRDColumnMetadata, teachingRelatedDutiesViewColumns } from './TeachingRelatedDutiesViewColumns';
import { Input, NumberInput } from '@/components/ui';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { Check, Info, PencilLine, X } from 'lucide-react';
import { trpc } from '@/data-loaders';
import { AcademicTeachingRelatedDutyDetails, TeachingRelatedDutyDetails } from '#server/models';
import { Cell } from '@tanstack/react-table';
import { useHandleSuccess } from '@/hooks/use-handle-success';
import { formatNumber } from '@/lib/utils';

export interface EditableAcademicTeachingRelatedDutyDetails extends AcademicTeachingRelatedDutyDetails {
  isEditable: boolean;
  readonly dataChangeCallback: (
    sender: React.ChangeEvent<HTMLInputElement>,
    cell: Cell<EditableAcademicTeachingRelatedDutyDetails, unknown>,
  ) => void;
}

export const UnitTeachingRelatedDuties = ({ unitWorkloadId }: { unitWorkloadId: number }) => {
  const sendToastAcademic = useHandleSuccess({
    title: 'Update academic teaching related duty hours',
  });
  const sendToastUnitWorkloadTeaching = useHandleSuccess({
    title: 'Update unit workload teaching related duty hours',
  });

  const handleError = useHandleError();
  const campusId = useAwmsStore((state) => state.selectedCampusId);
  const plannedStudents = useAwmsStore((state) => state.plannedStudents);
  const taughtWeeks = useAwmsStore((state) => state.taughtWeeks);

  const [academicTRDs, setAcademicTRDs] = useState<EditableAcademicTeachingRelatedDutyDetails[]>([]);
  const [originalAcademicTRDs, setOriginalAcademicTRDs] = useState<EditableAcademicTeachingRelatedDutyDetails[]>([]);

  const [teachingRelatedDuty, setTeachingRelatedDuty] = useState<TeachingRelatedDutyDetails | undefined>(undefined);
  const [originalTeachingRelatedDuty, setOriginalTeachingRelatedDuty] = useState<
    TeachingRelatedDutyDetails | undefined
  >(undefined);
  const {
    data: teachingRelatedDutyQueryData,
    error: teachingRelatedDutyQueryDataError,
    isLoading: isTeachingRelatedDutyQueryDataLoading,
  } = getUnitWorkloadTeachingHours(unitWorkloadId, campusId);

  const {
    data: academicTeachingRelatedDutyQueryData,
    error: academicTeachingRelatedDutyQueryDataError,
    isLoading: isAcademicTeachingRelatedDutyQueryDataLoading,
  } = getUnitWorkloadAcademicTeachings(unitWorkloadId, campusId);

  const columnNames = [
    ['UnitCoordination', 'Unit coordination'],
    ['Marking', 'Marking'],
    ['Preparation', 'Preparation'],
    ['CoAssessorDuties', 'Co-assessor duties'],
    ['Consultation', 'Consultation'],
    ['Moderation', 'Moderation'],
    ['Other', 'Other'],
    ['UnitDevelopment', 'Unit development'],
  ];

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isEditable, setIsEditable] = useState(false);
  const [alert, setAlert] = useState<boolean>(false);
  const componentRef = useRef<HTMLDivElement>(null);

  handleError([teachingRelatedDutyQueryDataError, academicTeachingRelatedDutyQueryDataError]);

  const validate = (input: string): number | undefined => {
    try {
      return Number.parseFloat(input ?? '0');
    } catch {
      return undefined;
    }
  };

  const recalculateCanAssign = (key: string) => {
    const justKey = key.substring(0, key.indexOf('OverrideHours'));
    if (teachingRelatedDuty) {
      teachingRelatedDuty[`${justKey}CanAssignHours` as keyof TeachingRelatedDutyDetails] = Number(
        (teachingRelatedDuty[key as keyof TeachingRelatedDutyDetails] ?? 0 > 0)
          ? teachingRelatedDuty[key as keyof TeachingRelatedDutyDetails]
          : teachingRelatedDuty[`${justKey}GuidelineHours` as keyof TeachingRelatedDutyDetails],
      );
    }
  };

  /// Handle change event for input elements, omit academicIndex if it's not an Academic TRD, to edit header details
  const handleChange = (event: ChangeEvent<HTMLInputElement>, key: string, academicIndex: number = -1) => {
    inputRef.current = event.target;
    const lastValue = event.target.dataset.lastValue;
    const currentValue = event.target.value;
    const originalValue = event.target.dataset.originalValue;

    if (lastValue !== currentValue) {
      let validatedCurrentValue = validate(currentValue);
      validatedCurrentValue = Number.isNaN(validatedCurrentValue) ? 0 : validatedCurrentValue;
      if (validatedCurrentValue !== undefined) {
        if (academicIndex < 0) {
          if (teachingRelatedDuty) {
            // @ts-expect-error: Any type for index.
            teachingRelatedDuty[key] = Number(validatedCurrentValue);
            recalculateCanAssign(key);
            setTeachingRelatedDuty({ ...teachingRelatedDuty });
          }
        } else {
          if (academicTRDs && academicTRDs.length > 0) {
            // @ts-expect-error: Any type for index.
            academicTRDs[academicIndex][key] = Number(validatedCurrentValue);
            setAcademicTRDs(
              [...academicTRDs].map((item) => ({
                ...item,
                isEditable: true,
                dataChangeCallback: handleAcademicTRDChanged,
              })),
            );
          }
        }
        event.target.value = validatedCurrentValue.toString();
        event.target.dataset.lastValue = validatedCurrentValue.toString();
        if (validatedCurrentValue !== validate(originalValue ?? '0')) event.target.dataset.changedId = (-1).toString();
      } else {
        event.target.value = lastValue ?? '';
        if (academicIndex < 0) {
          if (teachingRelatedDuty) {
            // @ts-expect-error: Any type for index.
            teachingRelatedDuty[key] = Number(lastValue);
            setTeachingRelatedDuty({ ...teachingRelatedDuty });
          }
        } else {
          if (academicTRDs && academicTRDs.length > 0) {
            // @ts-expect-error: Any type for index.
            academicTRDs[academicIndex][key] = Number(lastValue);
            setAcademicTRDs([...academicTRDs]);
          }
        }
        event.target.dataset.lastValue = lastValue;
      }
    }
  };

  const capitalizeFirstLetter = (value: string): string => {
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  };

  const handleAcademicTRDChanged = (
    sender: React.ChangeEvent<HTMLInputElement>,
    cell: Cell<EditableAcademicTeachingRelatedDutyDetails, unknown>,
  ) => {
    handleChange(sender, `trd${capitalizeFirstLetter(cell.column.id)}Hours`, cell.row.index);
  };

  const handleEditToggle = (enabled: boolean): void => {
    if (enabled) {
      // Store the original data
      setOriginalAcademicTRDs(JSON.parse(JSON.stringify(academicTRDs)));
      setOriginalTeachingRelatedDuty({ ...teachingRelatedDuty } as TeachingRelatedDutyDetails);
    }
    setAcademicTRDs(
      JSON.parse(JSON.stringify(academicTRDs)).map((item: AcademicTeachingRelatedDutyDetails) => ({
        ...item,
        isEditable: enabled,
        dataChangeCallback: handleAcademicTRDChanged,
      })),
    );
    setIsEditable(enabled);
  };

  const trpcUtils = trpc.useUtils();

  const handleInvalidateAndRefetch = async () => {
    await trpcUtils.trd.getAcademicDutyHours.invalidate({ unitWorkloadId, campusId });
    await trpcUtils.trd.getAcademicDutyHours.refetch();
    await trpcUtils.trd.getUnitWorkloadTeachingHours.invalidate({ unitWorkloadId, campusId });
    await trpcUtils.trd.getUnitWorkloadTeachingHours.refetch();
  };

  // Cancel editing and reset to original data
  const handleCancelEditing = () => {
    handleEditToggle(false);
    // Reset to original data
    setAcademicTRDs(
      JSON.parse(JSON.stringify(originalAcademicTRDs)).map((item: AcademicTeachingRelatedDutyDetails) => ({
        ...item,
        isEditable: false,
        dataChangeCallback: handleAcademicTRDChanged,
      })),
    );
    setTeachingRelatedDuty({ ...originalTeachingRelatedDuty } as TeachingRelatedDutyDetails);
  };

  // Update the teaching related duty details in the database
  const updateTrdDetailsMutation = trpc.trd.updateAcademicDutyHours.useMutation({
    onSuccess: () => {
      handleInvalidateAndRefetch();
      sendToastAcademic();
    },
    onError: (error) => {
      setTeachingRelatedDuty(undefined);
      console.error('Error updating teaching related duty details:', error);
      handleError([error]);
    },
  });

  // Update the teaching related duty header details in the database
  const updateTrdHeaderDetailsMutation = trpc.trd.updateUnitWorkloadTeachingHours.useMutation({
    onSuccess: () => {
      handleInvalidateAndRefetch();
      sendToastUnitWorkloadTeaching();
    },
    onError: (error) => {
      setTeachingRelatedDuty(undefined);
      console.error('Error updating teaching related duty header details:', error);
      handleError([error]);
    },
  });

  const getChangedAcademicTeachingRelatedDutyIds = (): Array<number> => {
    return academicTRDs
      .filter(
        (trd, index) =>
          !Object.keys(trd)
            .filter((key) => key !== 'isEditable' && key !== 'dataChangeCallback')
            .every(
              (key) =>
                originalAcademicTRDs[index] &&
                trd[key as keyof EditableAcademicTeachingRelatedDutyDetails] ===
                  originalAcademicTRDs[index][key as keyof EditableAcademicTeachingRelatedDutyDetails],
            ),
      )
      .map((trd) => trd.id);
  };

  const getChangedTeachingRelatedDutyIds = (): boolean => {
    return teachingRelatedDuty
      ? !Object.keys(teachingRelatedDuty).every(
          (key) =>
            teachingRelatedDuty[key as keyof TeachingRelatedDutyDetails] ===
              originalTeachingRelatedDuty?.[key as keyof TeachingRelatedDutyDetails] || key == 'id',
        )
      : false;
  };

  const handleSubmitEditing = () => {
    const teachingDutyChanged = getChangedTeachingRelatedDutyIds();
    const changedTRDIds = getChangedAcademicTeachingRelatedDutyIds();

    if (!(teachingDutyChanged || changedTRDIds.length > 0)) {
      handleEditToggle(false);
      return;
    }

    if (teachingDutyChanged) {
      updateTrdHeaderDetailsMutation.mutate({
        trdUpdatedHeaderDetails: teachingRelatedDuty as TeachingRelatedDutyDetails,
        unitWorkloadId,
      });
    }

    if (changedTRDIds.length > 0) {
      updateTrdDetailsMutation.mutate({
        trdUpdatedDetails: academicTRDs.filter((trd) => changedTRDIds.includes(trd.id)),
        unitWorkloadId,
      });
    }

    handleEditToggle(false);
  };

  // Set the academic teaching related duties
  useEffect(() => {
    if (
      !isAcademicTeachingRelatedDutyQueryDataLoading &&
      academicTeachingRelatedDutyQueryData &&
      academicTeachingRelatedDutyQueryData.length > 0
    )
      setAcademicTRDs(
        academicTeachingRelatedDutyQueryData.map((item) => ({
          ...item,
          isEditable: isEditable,
          dataChangeCallback: handleAcademicTRDChanged,
        })),
      );
    // We do not want to include functions in the dependencies, this breaks things.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAcademicTeachingRelatedDutyQueryDataLoading, academicTeachingRelatedDutyQueryData, isEditable]);

  // Set the teaching related duty details
  useEffect(() => {
    if (!isTeachingRelatedDutyQueryDataLoading && teachingRelatedDutyQueryData) {
      const data = {
        id: teachingRelatedDutyQueryData.id,
        trdUnitCoordinationCanAssignHours:
          (teachingRelatedDutyQueryData.trdUnitCoordinationOverrideHours ?? 0) > 0
            ? teachingRelatedDutyQueryData.trdUnitCoordinationOverrideHours
            : teachingRelatedDutyQueryData.trdUnitCoordinationGuidelineHours,
        trdUnitCoordinationGuidelineHours: teachingRelatedDutyQueryData.trdUnitCoordinationGuidelineHours,
        trdUnitCoordinationOverrideHours: teachingRelatedDutyQueryData.trdUnitCoordinationOverrideHours,

        trdMarkingCanAssignHours:
          (teachingRelatedDutyQueryData.trdMarkingOverrideHours ?? 0) > 0
            ? teachingRelatedDutyQueryData.trdMarkingOverrideHours
            : (teachingRelatedDutyQueryData.trdMarkingGuidelineHours ?? 0) * plannedStudents,
        trdMarkingGuidelineHours: teachingRelatedDutyQueryData.trdMarkingGuidelineHours,
        trdMarkingOverrideHours: teachingRelatedDutyQueryData.trdMarkingOverrideHours,

        trdPreparationCanAssignHours:
          (teachingRelatedDutyQueryData.trdPreparationOverrideHours ?? 0) > 0
            ? teachingRelatedDutyQueryData.trdPreparationOverrideHours
            : (teachingRelatedDutyQueryData.trdPreparationGuidelineHours ?? 0) * taughtWeeks,
        trdPreparationGuidelineHours: teachingRelatedDutyQueryData.trdPreparationGuidelineHours,
        trdPreparationOverrideHours: teachingRelatedDutyQueryData.trdPreparationOverrideHours,

        trdCoAssessorDutiesCanAssignHours: teachingRelatedDutyQueryData.trdCoAssessorDutiesCanAssignHours,
        trdCoAssessorDutiesGuidelineHours: teachingRelatedDutyQueryData.trdCoAssessorDutiesGuidelineHours,
        trdCoAssessorDutiesOverrideHours: teachingRelatedDutyQueryData.trdCoAssessorDutiesOverrideHours,

        trdConsultationCanAssignHours:
          (teachingRelatedDutyQueryData.trdConsultationOverrideHours ?? 0) > 0
            ? teachingRelatedDutyQueryData.trdConsultationOverrideHours
            : (teachingRelatedDutyQueryData.trdConsultationGuidelineHours ?? 0) * taughtWeeks,
        trdConsultationGuidelineHours: teachingRelatedDutyQueryData.trdConsultationGuidelineHours,
        trdConsultationOverrideHours: teachingRelatedDutyQueryData.trdConsultationOverrideHours,

        trdModerationCanAssignHours:
          (teachingRelatedDutyQueryData.trdModerationOverrideHours ?? 0) > 0
            ? teachingRelatedDutyQueryData.trdModerationOverrideHours
            : (teachingRelatedDutyQueryData.trdModerationGuidelineHours ?? 0),
        trdModerationGuidelineHours: teachingRelatedDutyQueryData.trdModerationGuidelineHours,
        trdModerationOverrideHours: teachingRelatedDutyQueryData.trdModerationOverrideHours,

        trdOtherCanAssignHours: teachingRelatedDutyQueryData.trdOtherCanAssignHours,
        trdOtherGuidelineHours: teachingRelatedDutyQueryData.trdOtherGuidelineHours,
        trdOtherOverrideHours: teachingRelatedDutyQueryData.trdOtherOverrideHours,

        trdUnitDevelopmentCanAssignHours: teachingRelatedDutyQueryData.trdUnitDevelopmentCanAssignHours,
        trdUnitDevelopmentGuidelineHours: teachingRelatedDutyQueryData.trdUnitDevelopmentGuidelineHours,
        trdUnitDevelopmentOverrideHours: teachingRelatedDutyQueryData.trdUnitDevelopmentOverrideHours,
      } as TeachingRelatedDutyDetails;
      setTeachingRelatedDuty(data);
    }
  }, [isTeachingRelatedDutyQueryDataLoading, teachingRelatedDutyQueryData, plannedStudents, taughtWeeks]);

  // useEffect to handle outside click
  useEffect(() => {
    const changedTRDIds = getChangedAcademicTeachingRelatedDutyIds();
    const teachingDutyChanged = getChangedTeachingRelatedDutyIds();
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node) && isEditable) {
        if (changedTRDIds.length > 0 || teachingDutyChanged) setAlert(true);
        else {
          handleEditToggle(false);
          handleCancelEditing();
        }
      }
    };

    const handleClickOutsideWithList = (event: MouseEvent) => {
      if (teachingRelatedDuty) {
        handleClickOutside(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideWithList);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideWithList);
    };
    // We do not want to include functions in the dependencies, this breaks things.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicTRDs, teachingRelatedDuty, isEditable]);

  // Focus on the input element when editing is enabled
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current = null;
    }
  });

  if (
    (isAcademicTeachingRelatedDutyQueryDataLoading || isTeachingRelatedDutyQueryDataLoading) &&
    !teachingRelatedDutyQueryData
  ) {
    return <Loader />;
  }

  const renderCanAssignHours = (columnName: string | undefined): ReactNode => {
    const value = teachingRelatedDuty?.[`trd${columnName}GuidelineHours` as keyof TeachingRelatedDutyDetails] ?? 0;
    const overrideValue =
      teachingRelatedDuty?.[`trd${columnName}OverrideHours` as keyof TeachingRelatedDutyDetails] ?? 0;

    if (overrideValue > 0) {
      return <div>{`${formatNumber(overrideValue)} hours`}</div>;
    }
    switch (columnName) {
      // flat per unit
      case 'UnitCoordination':
      case 'Moderation':
        return <div>{`${formatNumber(value)} hours`}</div>;

      // per teaching week
      case 'Preparation':
      case 'Consultation':
        return <div>{`${formatNumber(value * taughtWeeks)} hours`}</div>;

      // per student
      case 'Marking':
        return <div>{`${formatNumber(value * plannedStudents)} hours`}</div>;

      // not applicable (co-assessor duties, other, unit development)
      default:
        return <div>{'-'}</div>;
    }
  };

  const renderGuidelineHours = (columnName: string | undefined): ReactNode => {
    const value = teachingRelatedDuty?.[`trd${columnName}GuidelineHours` as keyof TeachingRelatedDutyDetails] ?? 0;
    const overrideValue =
      teachingRelatedDuty?.[`trd${columnName}OverrideHours` as keyof TeachingRelatedDutyDetails] ?? 0;
    switch (columnName) {
      // flat per unit
      case 'UnitCoordination':
      case 'Moderation':
        return <div className={overrideValue ? 'line-through' : ''}>{`${formatNumber(value)} hours`}</div>;

      // per teaching week
      case 'Preparation':
      case 'Consultation':
        return (
          <div
            title={`(${formatNumber(value)} hours per teaching week)`}
            className={`flex cursor-help items-center justify-between ${overrideValue ? 'line-through' : ''}`}
          >
            {`${formatNumber(value * taughtWeeks)} hours `}
            <IconComponent className="mr-1 inline size-4" IconType={Info} />
          </div>
        );

      // per student
      case 'Marking':
        return (
          <div
            title={`(${formatNumber(value)} hours per student)`}
            className={`flex cursor-help items-center justify-between ${overrideValue ? 'line-through' : ''}`}
          >
            {`${formatNumber(value * plannedStudents)} hours `}
            <IconComponent className="mr-1 inline size-4" IconType={Info} />
          </div>
        );

      // not applicable (co-assessor duties, other, unit development)
      default:
        return <div>{'-'}</div>;
    }
  };

  return (
    <>
      <AlertModal
        title="Unsaved changes"
        description="You have unsaved changes. Please save them before navigating away."
        confirmAction={() => {
          setAlert(false);
        }}
        open={alert}
      />
      <div ref={componentRef} className={'pl-15 flex basis-4/5 flex-col gap-5 bg-white py-5 pr-1 xl:pr-[143px]'}>
        <h3 className="pb-4 text-sh3 font-bold">
          Teaching related duties (TRD){' '}
          {isEditable ? (
            <>
              <Button size="icon" variant="filled-secondary" className="ml-2 h-8 w-8 p-0" onClick={handleCancelEditing}>
                <IconComponent IconType={X} />
              </Button>{' '}
              <Button size="icon" variant="filled-secondary" className="h-8 w-8 p-0" onClick={handleSubmitEditing}>
                <IconComponent IconType={Check} />
              </Button>
            </>
          ) : (
            <Button
              size="icon"
              variant="filled-secondary"
              className="ml-2 h-8 w-8 p-0"
              onClick={() => {
                handleEditToggle(true);
              }}
            >
              <IconComponent IconType={PencilLine} />
            </Button>
          )}
        </h3>
        {
          <DataTable
            id="trd-table"
            columns={teachingRelatedDutiesViewColumns.map((col) => ({
              ...col,
              meta: {
                ...col.meta,
                custom: {
                  trd: teachingRelatedDuty,
                  plannedStudents: plannedStudents,
                  taughtWeeks: taughtWeeks,
                } as CustomTRDColumnMetadata,
              },
            }))}
            data={academicTRDs?.length ? academicTRDs : []}
            footerProps={{ className: 'border-t-2 border-t-deep-blue', disablePagination: true }}
            headerProps={{ primaryColor: 'bg-white', disableDefaultBorder: true }}
            customHeaderSlot={
              academicTRDs?.length ? (
                <>
                  <tr
                    className="h-5 !border-t-0 [&_div]:p-[10px] [&_div]:pt-0 [&_div]:text-left [&_div]:text-b3 [&_div]:font-bold
                      [&_div]:text-primary-black-90"
                  >
                    <th key={`trdAssigneeCanAssignHours`}>
                      <div className="!font-normal !text-secondary-grey-02">Can assign</div>
                    </th>
                    {columnNames.map((name) => (
                      <th key={`trd${name[0]?.toString()}CanAssignHours`}>{renderCanAssignHours(name[0])}</th>
                    ))}
                  </tr>
                  <tr
                    className="h-5 !border-b-0 bg-deep-blue-50 [&_div]:p-[10px] [&_div]:py-1 [&_div]:pr-[10px] [&_div]:text-left [&_div]:text-b3
                      [&_div]:font-normal [&_div]:text-secondary-grey-02"
                  >
                    <th key={`trdAssigneeGuidelineHours`}>
                      <div>Guideline</div>
                    </th>
                    {columnNames.map((name) => (
                      <th key={`trd${name[0]?.toString()}GuidelineHours`}>{renderGuidelineHours(name[0])}</th>
                    ))}
                  </tr>
                  <tr
                    className="h-5 !border-t-0 border-b border-b-deep-blue bg-deep-blue-50 align-top [&_div]:p-[10px] [&_div]:pb-[10px]
                      [&_div]:pr-[10px] [&_div]:pt-1 [&_div]:text-left [&_div]:text-b3 [&_div]:font-normal [&_div]:text-secondary-grey-02
                      [&_input:invalid]:border [&_input:invalid]:border-red-600 [&_input:invalid]:bg-red-100 [&_input:invalid]:text-red-600
                      [&_input]:bg-white [&_input]:px-[19px] [&_input]:py-3.5 [&_input]:font-normal [&_input]:text-secondary-grey-01"
                  >
                    <th key={`trdAssigneeOverrideHours`}>
                      <div className="spc !font-bold">Override</div>
                    </th>
                    {columnNames.map((name) => (
                      <th key={`trd${name[0]?.toString()}OverrideHours`}>
                        <div className="spc">
                          <NumberInput
                            key={`trd${name[0]?.toString()}OverrideHours`}
                            ref={inputRef}
                            disabled={!isEditable}
                            value={
                              teachingRelatedDuty
                                ? (teachingRelatedDuty[
                                    `trd${name[0]?.toString()}OverrideHours` as keyof TeachingRelatedDutyDetails
                                  ] ?? 0)
                                : 0
                            }
                            onChange={(sender) => handleChange(sender, `trd${name[0]?.toString()}OverrideHours`)}
                            data-key="trdConsultationOverrideHours"
                            data-index="0"
                            data-original-value={
                              teachingRelatedDuty
                                ? (teachingRelatedDuty[
                                    `trd${name[0]?.toString()}OverrideHours` as keyof TeachingRelatedDutyDetails
                                  ] ?? 0)
                                : 0
                            }
                            decimalScale={2}
                            min={0}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </>
              ) : (
                <></>
              )
            }
          />
        }
      </div>
    </>
  );
};
